/**
 * キオスク DPC (LUCA) のバッテリーテレメトリ。
 *
 * DPC が window.KioskBridge (addJavascriptInterface) で測定値を差し出し、送信者は
 * この配信 Web ただ一人 (既存 heartbeat 1 本に同梱)。ブリッジ無し環境 (非 DPC 端末 =
 * 電池レス / 旧 APK / ブラウザ) では全 API が undefined を返し、何も起きない。
 *
 * 警報はイベント駆動: 定期 heartbeat (起動時+24h) とは別に、ローカル監視 (通信なし) が
 * 閾値超過を検知した時だけ臨時 heartbeat を送る。閾値は Rails KioskConfig 正本 →
 * DPC config ポーリング → ブリッジ応答、の経路で届く (本アプリの再デプロイ不要)。
 *
 * 設計: adbox-admin-rails docs/design/features/device_telemetry.md §10
 */

/** ブリッジが返す測定値 (camelCase は DPC 側 BatteryMonitor.snapshotJson と一致)。 */
export interface KioskBatteryInfo {
  level?: number; // 残量 %
  temperature?: number; // ℃ (現在)
  temperatureMax?: number; // ℃ (前回報告以降のピーク)
  voltageMv?: number;
  charging: boolean;
  alertTempC: number; // 警報閾値 ℃ (KioskConfig 由来)
  alertDropPctPerHour: number; // 警報閾値 %/時 (同上)
}

/** heartbeat payload に載せる形 (閾値は送らない)。 */
export interface BatteryPayload {
  level?: number;
  temperature?: number;
  temperatureMax?: number;
  voltageMv?: number;
  charging: boolean;
  alert: boolean;
  /** 給電の入・切の報告のときだけ付く (定期便・閾値警報では付けない) */
  trigger?: PowerTrigger;
  /** 給電の入・切を観測した時刻 (ISO 8601)。送信が遅れても実時刻を残すため */
  observedAt?: string;
}

interface KioskBridgeLike {
  getBatteryInfo?: () => string | null;
  markReported?: () => void;
  getKioskInfo?: () => string | null;
}

/** キオスク自身の状態 (DPC KioskBridge.getKioskInfo と一致)。config 到達・APK 版の可視化用。 */
export interface KioskInfo {
  appliedConfigVersion: number;
  apkVersionCode: number;
}

const getBridge = (): KioskBridgeLike | undefined =>
  (window as unknown as { KioskBridge?: KioskBridgeLike }).KioskBridge;

/** ブリッジから測定値を読む。無い/不正 JSON/許可外 (null) は undefined。 */
export const readKioskBattery = (): KioskBatteryInfo | undefined => {
  try {
    const raw = getBridge()?.getBatteryInfo?.();
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as Partial<KioskBatteryInfo>;
    if (
      typeof parsed.alertTempC !== "number" ||
      typeof parsed.alertDropPctPerHour !== "number"
    ) {
      return undefined;
    }
    return {
      level: typeof parsed.level === "number" ? parsed.level : undefined,
      temperature:
        typeof parsed.temperature === "number" ? parsed.temperature : undefined,
      temperatureMax:
        typeof parsed.temperatureMax === "number"
          ? parsed.temperatureMax
          : undefined,
      voltageMv:
        typeof parsed.voltageMv === "number" ? parsed.voltageMv : undefined,
      charging: parsed.charging === true,
      alertTempC: parsed.alertTempC,
      alertDropPctPerHour: parsed.alertDropPctPerHour,
    };
  } catch {
    return undefined; // ブリッジ由来の想定外は全て「無かったこと」に (表示動作へ波及させない)
  }
};

/**
 * ブリッジからキオスク状態 (適用済み configVersion / APK versionCode) を読む。
 * heartbeat に同梱され、管理画面の物理端末一覧で「config が届いているか」を可視化する。
 * ブリッジ無し (非 DPC / 旧 APK) や不正応答は undefined。
 */
export const readKioskInfo = (): KioskInfo | undefined => {
  try {
    const raw = getBridge()?.getKioskInfo?.();
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as Partial<KioskInfo>;
    if (
      typeof parsed.appliedConfigVersion !== "number" ||
      typeof parsed.apkVersionCode !== "number"
    ) {
      return undefined;
    }
    return {
      appliedConfigVersion: parsed.appliedConfigVersion,
      apkVersionCode: parsed.apkVersionCode,
    };
  } catch {
    return undefined;
  }
};

/** heartbeat 送信成功後に呼ぶ: DPC 側のピーク温度をリセットする (失敗時は呼ばない契約)。 */
export const markKioskBatteryReported = (): void => {
  try {
    getBridge()?.markReported?.();
  } catch {
    // ベストエフォート
  }
};

/** 測定値 → heartbeat payload (閾値を落とし alert フラグを付ける)。 */
export const toBatteryPayload = (
  info: KioskBatteryInfo,
  alert: boolean
): BatteryPayload => ({
  level: info.level,
  temperature: info.temperature,
  temperatureMax: info.temperatureMax,
  voltageMv: info.voltageMv,
  charging: info.charging,
  alert,
});

// ---- 警報判定 (純ロジック。時刻は外から渡しテスト可能にする) ----

/** 温度警報の再送抑制: 前回警報から +2℃ 更新するか 60 分経過するまで再送しない。 */
export const TEMP_HYSTERESIS_C = 2;
export const ALERT_COOLDOWN_MS = 60 * 60 * 1000;
/** 放電速度の判定に最低限必要な観測窓 (これ未満の間隔ではノイズが大きく判定しない)。 */
export const DROP_MIN_WINDOW_MS = 10 * 60 * 1000;
/** 放電アンカーの最大寿命。古い基準点との比較で平均化されて鈍らないよう定期的に張り替える。 */
export const DROP_MAX_WINDOW_MS = 2 * 60 * 60 * 1000;

export type BatteryAlertReason = "temperature" | "drop";

export interface BatteryAlertResult {
  alert: boolean;
  reason?: BatteryAlertReason;
}

interface DischargeAnchor {
  level: number;
  atMs: number;
}

/**
 * 閾値監視の状態機械。evaluate(info, nowMs) を定期的に呼び、臨時 heartbeat を送るべき
 * 瞬間だけ alert: true を返す。
 *
 *   - 温度: temperature >= alertTempC。ヒステリシス付き (再送は +2℃ 更新 or 60分経過)。
 *   - 急落: 非給電中の放電速度 (%/時) >= alertDropPctPerHour。給電が始まったら基準点を
 *     リセット (充電中の増減は劣化と無関係)。急落警報にも 60 分クールダウン。
 */
export class BatteryAlertMonitor {
  private lastTempAlertC: number | null = null;
  private lastTempAlertAtMs: number | null = null;
  private lastDropAlertAtMs: number | null = null;
  private anchor: DischargeAnchor | null = null;

  evaluate(info: KioskBatteryInfo, nowMs: number): BatteryAlertResult {
    const temp = this.evaluateTemperature(info, nowMs);
    if (temp.alert) return temp;
    return this.evaluateDrop(info, nowMs);
  }

  private evaluateTemperature(
    info: KioskBatteryInfo,
    nowMs: number
  ): BatteryAlertResult {
    const t = info.temperature;
    if (t === undefined || t < info.alertTempC) return { alert: false };

    const escalated =
      this.lastTempAlertC !== null &&
      t >= this.lastTempAlertC + TEMP_HYSTERESIS_C;
    const cooledDown =
      this.lastTempAlertAtMs !== null &&
      nowMs - this.lastTempAlertAtMs >= ALERT_COOLDOWN_MS;
    const first = this.lastTempAlertAtMs === null;

    if (!first && !escalated && !cooledDown) return { alert: false };

    this.lastTempAlertC = t;
    this.lastTempAlertAtMs = nowMs;
    return { alert: true, reason: "temperature" };
  }

  private evaluateDrop(
    info: KioskBatteryInfo,
    nowMs: number
  ): BatteryAlertResult {
    const level = info.level;
    // 給電中 or 残量不明は放電判定の対象外。基準点を捨てて次の非給電区間から測り直す。
    if (info.charging || level === undefined) {
      this.anchor = null;
      return { alert: false };
    }

    if (!this.anchor) {
      this.anchor = { level, atMs: nowMs };
      return { alert: false };
    }

    const elapsedMs = nowMs - this.anchor.atMs;
    if (elapsedMs < DROP_MIN_WINDOW_MS) return { alert: false };

    const dropPct = this.anchor.level - level;
    const ratePerHour = (dropPct / elapsedMs) * 60 * 60 * 1000;

    // 窓が古くなったら基準点を張り替える (長時間の平均化で急落が鈍るのを防ぐ)。
    if (elapsedMs >= DROP_MAX_WINDOW_MS) {
      this.anchor = { level, atMs: nowMs };
    }

    if (ratePerHour < info.alertDropPctPerHour) return { alert: false };

    const cooledDown =
      this.lastDropAlertAtMs === null ||
      nowMs - this.lastDropAlertAtMs >= ALERT_COOLDOWN_MS;
    if (!cooledDown) return { alert: false };

    this.lastDropAlertAtMs = nowMs;
    this.anchor = { level, atMs: nowMs }; // 警報後は測り直し (同じ下落で連発しない)
    return { alert: true, reason: "drop" };
  }
}

// ---- 給電の入・切の報告 (純ロジック。時刻は外から渡しテスト可能にする) ----
//
// 施設はコンセントタイマーで夜間に給電を切るが、タイマーの時刻は施設ごとに違い管理側に無い。
// 定期便 (起動時 + 24h) だけでは朝の電池駆動 (正常) と「給電が戻らない」(異常) を区別できないので、
// 給電の入・切の瞬間を実時刻つきで報告し、施設ごとのタイマー時刻を実データから得る。
// 設計: adbox-admin-rails docs/design/features/device_telemetry.md §10「給電の入・切の報告」

export type PowerTrigger = "power_on" | "power_off";

export interface PowerEvent {
  trigger: PowerTrigger;
  observedAtMs: number;
}

/** 状態が変わったと認めるのに必要な連続観測回数 (差し込みの接触不良などの一瞬の揺れを拾わない)。 */
export const POWER_CONFIRM_OBSERVATIONS = 2;

/**
 * 給電状態 (charging) の変化を検知する。observe(charging, nowMs) を監視周期ごとに呼び、
 * 変化が確定した瞬間だけ PowerEvent を返す。
 *
 *   - 最初の観測は基準にするだけで何も返さない (ページ読み込み直後は起動時の定期便と重複するため)
 *   - 新しい状態が POWER_CONFIRM_OBSERVATIONS 回続いたら確定。観測時刻は「新しい状態を最初に
 *     見た時刻」にする (確定を待った分だけ時刻を遅らせない)
 */
export class PowerTransitionDetector {
  private confirmed: boolean | null = null;
  private candidate: boolean | null = null;
  private candidateSinceMs = 0;
  private candidateCount = 0;

  observe(charging: boolean, nowMs: number): PowerEvent | null {
    if (this.confirmed === null) {
      this.confirmed = charging;
      return null;
    }

    if (charging === this.confirmed) {
      this.candidate = null;
      this.candidateCount = 0;
      return null;
    }

    if (this.candidate !== charging) {
      this.candidate = charging;
      this.candidateSinceMs = nowMs;
      this.candidateCount = 0;
    }
    this.candidateCount += 1;
    if (this.candidateCount < POWER_CONFIRM_OBSERVATIONS) return null;

    this.confirmed = charging;
    this.candidate = null;
    this.candidateCount = 0;
    return {
      trigger: charging ? "power_on" : "power_off",
      observedAtMs: this.candidateSinceMs,
    };
  }
}

/**
 * 未送信の給電イベント。回線 (ルーター) も同じタイマーで切れる施設では、給電が切れた瞬間は
 * オフラインで送れない。電池切れでページごと落ちても翌朝に送れるよう localStorage に残す。
 * 送る測定値は観測した瞬間のもの (翌朝の値を付けると「給電が切れた時点の残量」にならない)。
 */
export interface PendingPowerEvent {
  trigger: PowerTrigger;
  observedAt: string;
  battery: BatteryPayload;
}

/** 溜めておく上限。何日も送れない端末で localStorage を肥大させない (古いものから捨てる)。 */
export const PENDING_POWER_EVENTS_MAX = 10;

export const toPendingPowerEvent = (
  event: PowerEvent,
  info: KioskBatteryInfo
): PendingPowerEvent => {
  const observedAt = new Date(event.observedAtMs).toISOString();
  return {
    trigger: event.trigger,
    observedAt,
    battery: {
      ...toBatteryPayload(info, false),
      trigger: event.trigger,
      observedAt,
    },
  };
};

export const enqueuePowerEvent = (
  queue: PendingPowerEvent[],
  event: PendingPowerEvent
): PendingPowerEvent[] => [...queue, event].slice(-PENDING_POWER_EVENTS_MAX);

/** localStorage から読んだ値を検証する (壊れた値・古い形は捨てる)。 */
export const parsePendingPowerEvents = (raw: unknown): PendingPowerEvent[] => {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (e): e is PendingPowerEvent =>
      !!e &&
      typeof e === "object" &&
      (e.trigger === "power_on" || e.trigger === "power_off") &&
      typeof e.observedAt === "string" &&
      !!e.battery &&
      typeof e.battery === "object"
  );
};
